import { headingsPlugin, MDXEditor, MDXEditorMethods, tablePlugin, listsPlugin, linkPlugin, quotePlugin, thematicBreakPlugin, ListsToggle, InsertTable, CreateLink, BlockTypeSelect, BoldItalicUnderlineToggles, UndoRedo, toolbarPlugin, linkDialogPlugin, markdownShortcutPlugin, directivesPlugin, AdmonitionDirectiveDescriptor, imagePlugin, codeBlockPlugin } from '@mdxeditor/editor'
import Box, { BoxProps } from '@mui/material/Box'
import GlobalStyles from '@mui/material/GlobalStyles'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useTheme } from 'theme/useTheme'
import { useThemeStore } from 'theme/ThemeStore'
import '@mdxeditor/editor/style.css'
import { GapTimeDirectiveDescriptor } from './plugins/GapTimePlugin'
import { GapNoteDirectiveDescriptor, InsertGapNote } from './plugins/GapNotePlugin'
import { GapDeliveryNoteDirectiveDescriptor, InsertGapDeliveryNote } from './plugins/GapDeliveryNotePlugin'
import { GapFileDirectiveDescriptor } from './plugins/File/GapFile'
import { GapQuestionDirectiveDescriptor, gapQuestionProtectionPlugin } from './plugins/GapQuestionPlugin'
import { GapSessionHeaderDirectiveDescriptor, SessionLayoutContext, SessionPreambleMarker } from './plugins/GapSessionHeaderPlugin'
import { GapAssigneeDirectiveDescriptor } from './plugins/Assignee/GapAssigneePlugin'
import { mentionPlugin } from './plugins/Mention/MentionPlugin'
import GapFilePlugin from './plugins/File/GapFilePlugin'
import GapFilePluginStore from './plugins/File/GapFilePluginStore'
import { GapDurationDirectiveDescriptor } from './plugins/GapDurationPlugin'
import { GapImageDirectiveDescriptor } from './plugins/Image/GapImage'
import { GapMindsetDirectiveDescriptor } from './plugins/Image/GapMindset'
import { GapDateDirectiveDescriptor } from './plugins/Date/GapDatePlugin'
import { slashCommandPlugin } from './plugins/Slash/SlashCommandPlugin'
import GapImagePlugin from './plugins/Image/GapImagePlugin'
import GapImagePluginStore from './plugins/Image/GapImagePluginStore'
import GapMindsetPlugin from './plugins/Image/GapMindsetPlugin'
import GapMindsetPluginStore from './plugins/Image/GapMindsetPluginStore'
import useStore from 'reusable/mobx-store-utils/useStore'
import MarkdownStore, { MarkdownStoreProvider } from './MarkdownStore'
import { draggableBlockPlugin } from './plugins/DraggableBlockPlugin'
import { plainCodeBlockDescriptor } from './plugins/CodeBlock/PlainCodeBlockDescriptor'
import GapDialog from 'reusable/gap-dialog/GapDialog'
import FileUploadWithProgress from 'reusable/mui/file-uploads/FileUploadWithProgress'
import { fileValidation } from '@gap/utils/file/file-utils'
import MarkdownType from '@gap/backend/app-services/markdown/models/MarkdownType'
import MindsetImageTab from './plugins/Image/MindsetImageTab'
import { escapeStrayMdxSyntax } from '@gap/utils/markdown/markdown-escape'
import { readSessionLayout } from '@gap/shared/meetings/session-header'
import { stripTranscriptTimecodes } from '@gap/shared/meetings/transcript-renderer'
import MarkdownLoaderStatus, { MarkdownLoaderState, MARKDOWN_LOADING_CLASS } from './MarkdownLoaderStatus'
import { stripImagesForStreamedRender } from './markdownStreamingRender'

export interface MarkdownProps {
   markdown?: string
   onChange?: (markdown: string) => void
   readOnly?: boolean
   sx?: BoxProps['sx']
   /** Just hide the custom extensions like GapFile, GapImage, etc. */
   hidePlugins?: boolean
   /** Hides the `[mm:ss]` timecodes at the display layer; the canonical markdown keeps them. */
   hideTimecodes?: boolean
   /** Hide the toolbar completely */
   hideToolbar?: boolean
   /** Placeholder shown when the editor is empty and nothing is generating into it */
   placeholder?: string
   /** If of the entity. Used to keep track of uploaded file locations */
   id: string
   /** Experimental drag and drop */
   draggable?: boolean
   /** Auto saves to the markdown table given it's id and parameters */
   autoSave?: boolean
   /** Context for where this markdown editor is used */
   type: MarkdownType
   /** Reference to the editor instance, not required but handy when accesing this editor elsewhere */
   editorRef?: React.RefObject<MDXEditorMethods>
   /** Ref that receives a function to programmatically open the image upload dialog */
   openImageUploadRef?: React.MutableRefObject<(() => void) | null>
   /** Ref that receives a function to programmatically open the mindset picker dialog */
   openMindsetUploadRef?: React.MutableRefObject<(() => void) | null>
   /** Ref that receives a function to programmatically open the file upload dialog */
   openFileUploadRef?: React.MutableRefObject<(() => void) | null>
   /** Drives the AI status bar, the feather fade and the mid-generation editor lock. Omit where nothing generates. */
   loaderState?: MarkdownLoaderState
   /** Text shown in the status bar. Ignored unless `loaderState` is given. */
   loaderLabel?: string | null
   /** Dismisses the status bar. Only rendered for the error state — see `MarkdownLoaderStatus`. */
   onLoaderDismiss?: () => void
   /** Users in the meeting host org (advisor). Used by the @ mention picker. */
   users?: ({ name: string, id: string, deactivatedOn?: string | null } | { id: string, firstName?: string, lastName?: string, deactivatedOn?: string | null })[]
   /** Users belonging to the meeting's selected client org. Shown as a separate group in the picker. */
   clientUsers?: ({ id: string, firstName?: string, lastName?: string, deactivatedOn?: string | null })[]
   /** Display name for the client group header in the @ picker. */
   clientOrgName?: string
}

export default observer(function Markdown(props: MarkdownProps) {
   const theme = useTheme()
   const themeStore = useThemeStore()
   const dark = themeStore.dark
   const internalEditorRef = useRef<MDXEditorMethods>(null)
   const editorRef = props.editorRef || internalEditorRef
   // Streaming re-parses the whole document per chunk, which recreates every <img> and makes it strobe.
   const streamedMarkdown = props.markdown !== undefined && props.loaderState === 'loading'
      ? stripImagesForStreamedRender(props.markdown)
      : props.markdown
   // Escapes stray MDX syntax before the strict parser sees it, and strips timecodes for display.
   const safeMarkdown = useMemo(() => {
      if (streamedMarkdown === undefined) return undefined
      const escaped = escapeStrayMdxSyntax(streamedMarkdown)
      return props.hideTimecodes ? stripTranscriptTimecodes(escaped) : escaped
   }, [streamedMarkdown, props.hideTimecodes])
   const lastPropMarkdownRef = useRef(safeMarkdown)
   // The document's session structure, so each header derives its ordinal by counting (ADR 0001).
   const sessionLayout = useMemo(() => readSessionLayout(safeMarkdown ?? ''), [safeMarkdown])

   const markdownStore = useStore(sp => new MarkdownStore(sp), props)

   // Adapter: converts insertDirective calls to editorRef.insertMarkdown, so plugin stores
   // can work outside MDXEditor's toolbar (where usePublisher(insertDirective$) isn't available)
   const insertDirectiveAdapter = useCallback((directive: { name: string; type: string; attributes?: Record<string, string> }) => {
      const editor = editorRef.current
      if (!editor) return
      editor.focus()
      const prefix = directive.type === 'leafDirective' ? '::' : directive.type === 'textDirective' ? ':' : ':::'
      const attrs = directive.attributes
         ? '{' + Object.entries(directive.attributes).map(([k, v]) => `${k}="${v}"`).join(' ') + '}'
         : ''
      editor.insertMarkdown(`${prefix}${directive.name}${attrs}`)
   }, [editorRef])

   const imageUploadStore = useStore(sp => new GapImagePluginStore(sp), { insertDirective: insertDirectiveAdapter, markdownStore })
   const mindsetUploadStore = useStore(sp => new GapMindsetPluginStore(sp), { insertDirective: insertDirectiveAdapter })
   const fileUploadStore = useStore(sp => new GapFilePluginStore(sp), { insertDirective: insertDirectiveAdapter, orgId: '', markdownStore })

   useEffect(() => {
      if (props.openImageUploadRef) {
         props.openImageUploadRef.current = imageUploadStore.modalState.open
      }
      if (props.openMindsetUploadRef) {
         props.openMindsetUploadRef.current = mindsetUploadStore.modalState.open
      }
      if (props.openFileUploadRef) {
         props.openFileUploadRef.current = fileUploadStore.modalState.open
      }
   }, [props.openImageUploadRef, props.openMindsetUploadRef, props.openFileUploadRef, imageUploadStore.modalState.open, mindsetUploadStore.modalState.open, fileUploadStore.modalState.open])

   useEffect(() => {
      if (safeMarkdown !== lastPropMarkdownRef.current) {
         lastPropMarkdownRef.current = safeMarkdown
         if (editorRef.current && safeMarkdown !== undefined && safeMarkdown !== markdownStore.lastEditorMarkdown) {
            markdownStore.applyExternalMarkdown({ editor: editorRef.current, markdown: safeMarkdown })
         }
         markdownStore.lastEditorMarkdown = undefined
      }
   }, [safeMarkdown, markdownStore, editorRef])

   if (safeMarkdown === undefined) {
      return null
   }

   const hasLoader = props.loaderState !== undefined
   const hasPreambleMarker = sessionLayout.hasPreamble && sessionLayout.hasMultipleSessions
   const sx = Object.assign({}, getMarkDownSx({ dark, featherFade: hasLoader, hasPreambleMarker }), props.sx)

   return <MarkdownStoreProvider store={markdownStore}>
      <Box className={markdownStore.contentClass} sx={sx} onClick={markdownStore.handleLinkClick}>
         <GlobalStyles styles={{ '.mdxeditor-popup-container': { zIndex: `${theme.zIndex.modal + 1} !important` } }} />
         <SessionLayoutContext.Provider value={sessionLayout}>
         <SessionPreambleMarker layout={sessionLayout} />
         <MDXEditor
            ref={editorRef}
            className={dark ? 'dark-theme' : undefined}
            markdown={safeMarkdown}
            onChange={markdownStore.handleChange}
            onError={markdownStore.handleError}
            readOnly={markdownStore.isReadOnly}
            placeholder={markdownStore.placeholder}
            plugins={[
               headingsPlugin(),
               listsPlugin(),
               quotePlugin(),
               thematicBreakPlugin(),
               // Catch-all code-block support: without it MDXEditor throws on import for any
               // fenced/indented code block (e.g. an AI deliverable wrapped in a ```markdown
               // fence), blanking the whole preview. The descriptor renders every language as
               // read-only preformatted text instead of crashing.
               codeBlockPlugin({ defaultCodeBlockLanguage: '', codeBlockEditorDescriptors: [plainCodeBlockDescriptor] }),
               linkPlugin(),
               tablePlugin(),
               linkDialogPlugin(),
               markdownShortcutPlugin(),
               imagePlugin(),
               directivesPlugin({
                  directiveDescriptors: [
                     AdmonitionDirectiveDescriptor,
                     GapTimeDirectiveDescriptor,
                     GapDurationDirectiveDescriptor,
                     GapNoteDirectiveDescriptor,
                     GapDeliveryNoteDirectiveDescriptor,
                     GapFileDirectiveDescriptor,
                     GapImageDirectiveDescriptor,
                     GapMindsetDirectiveDescriptor,
                     GapQuestionDirectiveDescriptor,
                     GapSessionHeaderDirectiveDescriptor,
                     GapAssigneeDirectiveDescriptor,
                     GapDateDirectiveDescriptor,
                  ],
                  escapeUnknownTextDirectives: true
               }),
               gapQuestionProtectionPlugin(),
               mentionPlugin(),
               slashCommandPlugin(),
               ...(props.draggable ? [draggableBlockPlugin()] : []),
               ...(!props.hideToolbar ? [
                  toolbarPlugin({
                     toolbarContents: () => <>
                        <UndoRedo />
                        <BoldItalicUnderlineToggles />
                        <BlockTypeSelect />
                        {props.hidePlugins ? null : <>
                           <CreateLink />
                           <ListsToggle />
                           <InsertTable />
                           <InsertGapNote />
                           <InsertGapDeliveryNote />
                           <GapFilePlugin />
                           <GapImagePlugin />
                           <GapMindsetPlugin />
                        </>}
                     </>
                  }),] : [])
            ]}
         />
         </SessionLayoutContext.Provider>
         {/* Only for editors that opted into the loader — see `loaderState` on MarkdownProps. */}
         {hasLoader && <MarkdownLoaderStatus label={props.loaderLabel} state={markdownStore.loaderState} onDismiss={props.onLoaderDismiss} />}
      </Box >
      <GapDialog modalState={imageUploadStore.modalState} size="small" title="Add image">
         <FileUploadWithProgress
            files={imageUploadStore.file ? [imageUploadStore.file] : []}
            getPresignedUrl={imageUploadStore.getPresignedUrl}
            onFilesValidated={imageUploadStore.onFilesValidated}
            subtext="Upload an image"
            maxFiles={1}
            maxSize={fileValidation['markdown-files'].maxSizeBytes}
            appendUlids
            accept={{ 'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'] }}
         />
      </GapDialog>
      <GapDialog modalState={mindsetUploadStore.modalState} size="small" title="Add mindset">
         <MindsetImageTab onAdd={mindsetUploadStore.insertMindsetImage} />
      </GapDialog>
      <GapDialog modalState={fileUploadStore.modalState} size="small" title="Upload file">
         <FileUploadWithProgress
            files={fileUploadStore.file ? [fileUploadStore.file] : []}
            getPresignedUrl={fileUploadStore.getPresignedUrl}
            onFilesValidated={fileUploadStore.onFilesValidated}
            subtext="Upload a file to attach to the document"
            maxFiles={1}
            appendUlids
         />
      </GapDialog>
   </MarkdownStoreProvider>
})

const FEATHER_FADE_RAMP_PX = 100
/** Bottom edge held fully at 0% opacity, below the ramp. */
const FEATHER_FADE_CLEAR_PX = 50
const FEATHER_FADE_HEIGHT_PX = FEATHER_FADE_RAMP_PX + FEATHER_FADE_CLEAR_PX
/** A percentage, not a px stop, so the gradient holds its shape at every intermediate size. */
const FEATHER_FADE_CLEAR_STOP = `${Math.round((FEATHER_FADE_RAMP_PX / FEATHER_FADE_HEIGHT_PX) * 100)}%`
const FEATHER_FADE_TRANSITION = '400ms ease'

/**
 * Sizes the two feather mask layers, in `calc()` form so both states interpolate cleanly.
 * @param fadeHeightPx How tall the fade at the bottom edge should be.
 * @returns The `mask-size` value for the opaque block and the gradient below it.
 */
const getFeatherMaskSize = (fadeHeightPx: number) => `100% calc(100% - ${fadeHeightPx}px), 100% calc(${fadeHeightPx}px)`

/** Attached only to editors that pass `loaderState`, since a mask changes text antialiasing. */
const featherFadeAtRestSx = {
   maskImage: `linear-gradient(to bottom, #000, #000), linear-gradient(to bottom, #000, transparent ${FEATHER_FADE_CLEAR_STOP})`,
   maskRepeat: 'no-repeat, no-repeat',
   maskPosition: 'top, bottom',
   maskSize: getFeatherMaskSize(0),
   maskComposite: 'add',
   WebkitMaskComposite: 'source-over',
   transition: `mask-size ${FEATHER_FADE_TRANSITION}, -webkit-mask-size ${FEATHER_FADE_TRANSITION}`,
} as const

const getMarkDownSx = ({ dark, featherFade, hasPreambleMarker }: { dark: boolean, featherFade: boolean, hasPreambleMarker: boolean }): BoxProps['sx'] => ({
   ...(featherFade ? {
      [`&.${MARKDOWN_LOADING_CLASS} .mdxeditor .mdxeditor-root-contenteditable`]: {
         maskSize: getFeatherMaskSize(FEATHER_FADE_HEIGHT_PX),
      },
   } : {}),
   borderRadius: 1,
   height: '100%',
   // The preamble marker is a sibling of the editor, so the column has to share the height with it.
   ...(hasPreambleMarker ? { display: 'flex', flexDirection: 'column' } : {}),
   '& .mdxeditor': {
      ...(hasPreambleMarker ? { flex: 1, minHeight: 0 } : { height: '100%' }),
      display: 'flex',
      flexDirection: 'column',
      fontFamily: "DMSans",
   },
   '& .mdxeditor [role="toolbar"]': {
      position: 'sticky',
      top: 64,
      zIndex: 1,
      backgroundColor: dark ? '#252525' : '#fff',
      transition: 'opacity 0.2s ease-in-out, max-height 0.2s ease-in-out, padding 0.2s ease-in-out',
      overflow: 'hidden',
   },
   '&.toolbar-hidden .mdxeditor [role="toolbar"]': {
      opacity: 0,
      pointerEvents: 'none',
      maxHeight: 0,
      padding: 0,
   },
   '&.toolbar-visible .mdxeditor [role="toolbar"]': {
      opacity: 1,
      maxHeight: 100,
   },
   // Declared after the toolbar-visible rule so it wins when both classes are on.
   [`&.${MARKDOWN_LOADING_CLASS} .mdxeditor [role="toolbar"]`]: {
      opacity: 0.5,
      pointerEvents: 'none',
   },
   // The text I-beam over a document that won't accept text reads as "editable, but broken".
   [`&.${MARKDOWN_LOADING_CLASS} .mdxeditor [contenteditable]`]: {
      cursor: 'default',
   },
   '& .mdxeditor .mdxeditor-root-contenteditable': {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      ...(featherFade ? featherFadeAtRestSx : {}),
   },
   '& .mdxeditor .mdxeditor-root-contenteditable > div': {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
   },
   '& .mdxeditor .mdxeditor-root-contenteditable [contenteditable="true"]': {
      flex: 1,
   },
   '&.toolbar-visible .mdxeditor .mdxeditor-root-contenteditable > :first-of-type': {
      marginTop: 0,
   },
   // Let the empty-editor placeholder wrap instead of truncating with an
   // ellipsis on narrow editors (the agenda and right-hand notes panels).
   '& .mdxeditor [class*="_placeholder_"]': {
      whiteSpace: 'normal',
      overflow: 'visible',
      textOverflow: 'clip',
      width: '100%',
      boxSizing: 'border-box',
   },
   '& .mdxeditor li[role="checkbox"] > span': {
      position: 'relative',
      top: '-2px',
   },
   '& .mdxeditor li[role="checkbox"][aria-checked="true"]': {
      textDecoration: 'none !important',
   },
   '& .mdxeditor li[role="checkbox"][aria-checked="true"] span': {
      textDecoration: 'none !important',
   },
   '& .mdxeditor h2': {
      position: 'relative',
   },
   '& .mdxeditor blockquote': {
      margin: '1em 0',
      padding: '0.5em 1em',
      borderLeft: '4px solid #3b82f6',
      backgroundColor: dark ? '#1e293b' : '#f8fafc',
      color: dark ? '#94a3b8' : '#475569',
      fontStyle: 'italic',
   },
   '& .mdxeditor code': {
      color: dark ? '#fca5a5' : '#dc2626',
      backgroundColor: dark ? '#3b1111' : '#fef2f2',
      padding: '0.125em 0.25em',
      borderRadius: '0.25em',
      fontSize: '0.875em',
   },
   '& .mdxeditor pre.gap-code-block': {
      margin: '1em 0',
      padding: '12px 16px',
      borderRadius: '8px',
      overflowX: 'auto',
      fontSize: '0.8125em',
      lineHeight: 1.6,
      whiteSpace: 'pre',
      backgroundColor: dark ? '#252525' : '#f5f5f5',
      color: dark ? '#e6e6e6' : '#18001A',
      border: `1px solid ${dark ? '#3a3a3a' : '#e0e0e0'}`,
   },
   // Reset the inline-code pill styling above for code that sits inside a block.
   '& .mdxeditor pre.gap-code-block code': {
      color: 'inherit',
      backgroundColor: 'transparent',
      padding: 0,
      borderRadius: 0,
      fontSize: 'inherit',
   },
   '& .mdxeditor [class*="_admonition"]': {
      margin: '1em 0',
      padding: '1em',
      borderRadius: '6px',
      borderLeft: dark ? '4px solid #555' : '4px solid #d1d5db',
      backgroundColor: dark ? '#2a2a2a' : '#f9fafb',
   },
   '& .mdxeditor [class*="_admonition"] [class*="_nestedEditor"], & .mdxeditor [class*="_admonition"] [class*="_editorRoot"], & .mdxeditor [class*="_admonition"] [class*="_contentEditable"]': {
      backgroundColor: 'transparent',
   },
   '& .mdxeditor img': {
      maxWidth: '100%',
      maxHeight: 400,
      objectFit: 'contain',
   },
   '& .mdxeditor img[src*="/logo/"], & .mdxeditor img.advisor-logo': {
      maxWidth: '100%',
      maxHeight: 400,
      objectFit: 'contain',
   },
   '& .mdxeditor img.gap-image': {
      maxWidth: '100%',
      maxHeight: 'none',
   },
   '& .mdxeditor a': {
      textDecoration: 'underline',
   },
})
